/**
 * Knowledge base instant search.
 *
 * Reads the JSON index rendered by snippets/kb-nav.liquid
 * (#kb-search-index — {title, url, tags, excerpt} per article) and
 * scores it with a hand-rolled substring matcher. No fuzzy matching,
 * no dependency — the KB corpus is small (~20 articles), so a plain
 * indexOf-based scorer is plenty.
 *
 * Depends on the global `debounce` helper defined in global.js.
 */
class KbSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.resultsList = this.querySelector('[role="listbox"]');
    this.activeIndex = -1;
    this.index = null;
  }

  connectedCallback() {
    if (!this.input || !this.resultsList) return;

    this.boundOnInput =
      typeof debounce === 'function'
        ? debounce(this.onInput.bind(this), 80)
        : this.onInput.bind(this);
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.boundOnDocumentClick = this.onDocumentClick.bind(this);

    this.input.addEventListener('input', this.boundOnInput);
    this.input.addEventListener('keydown', this.boundOnKeydown);
    document.addEventListener('click', this.boundOnDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundOnDocumentClick);
  }

  getIndex() {
    if (this.index) return this.index;

    const script = document.getElementById('kb-search-index');
    try {
      this.index = script ? JSON.parse(script.textContent) : [];
    } catch (error) {
      this.index = [];
    }

    return this.index;
  }

  onInput() {
    const query = this.input.value.trim().toLowerCase();

    if (!query) {
      this.close();
      return;
    }

    const results = this.search(query).slice(0, 8);
    this.render(results, query);
  }

  search(query) {
    const categories = ['ATAK Plugins', 'General', 'GoTAK Trackers'];

    return this.getIndex()
      .map((entry) => {
        const title = (entry.title || '').toLowerCase();
        const tags = entry.tags || [];
        const excerpt = (entry.excerpt || '').toLowerCase();
        let score = 0;

        if (title === query) {
          score += 200;
        } else if (title.indexOf(query) === 0) {
          score += 130;
        } else if (title.indexOf(query) > -1) {
          score += 100;
        }

        if (tags.some((tag) => tag.toLowerCase().indexOf(query) > -1)) {
          score += 40;
        }

        if (excerpt.indexOf(query) > -1) {
          score += 10;
        }

        const category = tags.find((tag) => categories.indexOf(tag) > -1) || '';

        return { title: entry.title, url: entry.url, category, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score);
  }

  render(results, query) {
    this.activeIndex = -1;
    this.resultsList.innerHTML = '';

    if (!results.length) {
      const empty = document.createElement('li');
      empty.className = 'kb-search-empty';
      empty.textContent = 'No results for "' + query + '"';
      this.resultsList.appendChild(empty);
      this.open();
      return;
    }

    results.forEach((result, i) => {
      const option = document.createElement('li');
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.id = 'KbSearchResult-' + i;

      const link = document.createElement('a');
      link.className = 'kb-search-result';
      link.href = result.url;

      const title = document.createElement('span');
      title.className = 'kb-search-result__title';
      title.textContent = result.title;
      link.appendChild(title);

      if (result.category) {
        const meta = document.createElement('span');
        meta.className = 'kb-search-result__meta';
        meta.textContent = result.category;
        link.appendChild(meta);
      }

      option.appendChild(link);
      this.resultsList.appendChild(option);
    });

    this.open();
  }

  open() {
    this.resultsList.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.resultsList.hidden = true;
    this.resultsList.innerHTML = '';
    this.input.removeAttribute('aria-activedescendant');
    this.input.setAttribute('aria-expanded', 'false');
    this.activeIndex = -1;
  }

  onKeydown(event) {
    const options = this.resultsList.querySelectorAll('li[role="option"]');

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (!options.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = this.activeIndex < options.length - 1 ? this.activeIndex + 1 : 0;
      this.highlight(options);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = this.activeIndex > 0 ? this.activeIndex - 1 : options.length - 1;
      this.highlight(options);
    } else if (event.key === 'Enter' && this.activeIndex > -1) {
      event.preventDefault();
      const link = options[this.activeIndex].querySelector('a');
      if (link) window.location.href = link.href;
    }
  }

  highlight(options) {
    options.forEach((option, i) => {
      const isActive = i === this.activeIndex;
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) {
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }
}

customElements.define('kb-search', KbSearch);
